import { useEffect, useMemo, useState } from 'react';
import { downloadBlob } from '@idk-web/core-ui';
import { getInboxAttachment } from '@idk-web/api';
import { InboxMessageWithSigners } from '@/components/inbox/safemail/SafeMailInbox';

type Attachment = {
  name: string;
} & (
  | {
      state: 'LOADING';
    }
  | {
      state: 'LOADED';
      blob: Blob;
      url: string;
    }
  | {
      state: 'ERROR';
      error: string;
    }
);

export type Attachments = {
  name: string;
  state: 'LOADING' | 'LOADED' | 'ERROR';
  error?: string;
  asBlob(): Blob | null;
  asURL(): string | null;
  download(): void;
}[];

export function useAttachments(message: InboxMessageWithSigners): Attachments {
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    setAttachments((oldAttachments) => {
      for (const attachment of oldAttachments) {
        if (attachment.state === 'LOADED') {
          URL.revokeObjectURL(attachment.url);
        }
      }

      return [];
    });

    for (const attachment of message.attachments) {
      setAttachments((oldAttachments) => {
        return [...oldAttachments, { name: attachment.name, state: 'LOADING' }];
      });

      getInboxAttachment(message.id, attachment.name, controller.signal)
        .then((blob) => {
          if (controller.signal.aborted) {
            return;
          }

          setAttachments((oldAttachments) => {
            const newAttachment = {
              name: attachment.name,
              state: 'LOADED',
              blob,
              url: URL.createObjectURL(blob),
            } satisfies Attachment;
            const copy = oldAttachments.slice();
            const index = copy.findIndex((a) => a.name === attachment.name);

            if (index > -1) {
              copy[index] = newAttachment;
            } else {
              copy.push(newAttachment);
            }

            return copy;
          });
        })
        .catch((error) => {
          if (controller.signal.aborted) {
            return;
          }

          setAttachments((oldAttachments) => {
            const newAttachment = {
              name: attachment.name,
              state: 'ERROR',
              error,
            } satisfies Attachment;
            const copy = oldAttachments.slice();
            const index = copy.findIndex((a) => a.name === attachment.name);

            if (index > -1) {
              copy[index] = newAttachment;
            } else {
              copy.push(newAttachment);
            }

            return copy;
          });
        });
    }

    return () => controller.abort();
  }, [message]);

  return useMemo(
    () =>
      attachments.map((a) => ({
        name: a.name,
        state: a.state,
        error: a.state === 'ERROR' ? a.error : undefined,
        asBlob() {
          return a.state === 'LOADED' ? a.blob : null;
        },
        asURL() {
          return a.state === 'LOADED' ? a.url : null;
        },
        download() {
          const fileName = a.name || 'attachment';

          if (a.state === 'LOADED') {
            downloadBlob(a.blob, fileName);
          }
        },
      })),
    [attachments],
  );
}
